/**
 * @typedef {Object} TranslationType
 * @property {Number} id
 * @property {string} title
 * @property {string[]} languages
 * @property {string} status
 * @property {TranslationStatistics} statistics
 * @property {TranslationNotes} notes
 * @property {TranslationTimestamps} timestamps
 * @property {TranslationUser} client
 * @property {TranslationUser|null} assignee
 * @property {TranslationFiles} files
 */

/**
 * @typedef {Object} TranslationStatistics
 * @property {Number} translatedWordsCount
 */

/**
 * @typedef {Object} TranslationNotes
 * @property {string|null} byClient
 * @property {string|null} byAssignee
 */

/**
 * @typedef {Object} TranslationTimestamps
 * @property {string} createdAt
 * @property {string} updatedAt
 * @property {string|null} assignedAt
 * @property {string|null} completedAt
 * @property {string|null} deadline
 */

/**
 * @typedef {Object} TranslationUser
 * @property {Number} userId
 * @property {string} name
 * @property {string} email
 */

/**
 * @typedef {Object} TranslationFile
 * @property {string} title
 * @property {string} url
 * @property {string|null} size
 */

/**
 * @typedef {Object} TranslationFiles
 * @property {TranslationFile[]} toBeTranslated
 * @property {TranslationFile[]} support
 * @property {TranslationFile[]} translated
 */

/**
 * @type TranslationType
 */
export default class Translation {
    /** @param Object translationJson  */
    constructor(translationJson) {
        this.id = translationJson.id
        this.title = translationJson.title
        this.email = translationJson.email
        this.languages = translationJson.languages ?? []
        this.status = translationJson.status
        this.feedback = translationJson.feedback

        this.statistics = {}

        if (translationJson.statistics) {
            this.statistics.translatedWordsCount = translationJson.statistics.translated_words_count
        }

        this.notes = {}

        if (translationJson.notes) {
            this.notes.byAssignee = translationJson.notes.by_assignee ?? null
            this.notes.byClient = translationJson.notes.by_client ?? null    
        }

        this.timestamps = {}

        if (translationJson.timestamps) {
            this.timestamps.createdAt = translationJson.timestamps.created_at
            this.timestamps.updatedAt = translationJson.timestamps.updated_at
            this.timestamps.deadline = translationJson.timestamps.deadline ?? null
            this.timestamps.completedAt = translationJson.timestamps.completed_at ?? null
            this.timestamps.assignedAt = translationJson.timestamps.assigned_at ?? null    
        }

        this.client = {}
        
        if (translationJson.client) {
            this.client.userId = translationJson.client.user_id
            this.client.name = translationJson.client.name    
            this.client.email = translationJson.client.email    
        }

        if (translationJson.assignee) {
            this.assignee = {}
            this.assignee.userId = translationJson.assignee.user_id
            this.assignee.name = translationJson.assignee.name
        } else {
            this.assignee = null
        }

        this.files = {}
        if (translationJson.files) {
            this.files.toBeTranslated = translationJson.files.to_be_translated.map(fileJsonToFile)
            this.files.translated = translationJson.files.translated.map(fileJsonToFile)
            this.files.support = translationJson.files.support.map(fileJsonToFile)    
        }
    }

    static defaultNew() {
        return new this({
            title: null,
            statistics: {},
            assignee: {},
            client: {},
            notes: {},
            files: {
                to_be_translated: [],
                translated: [],
                support: [],
            },
        })
    }
}

// /** @enum {string} */
// const TranslationStatus = {
//     ToDo: 'TODO',
//     InProgress: 'IN_PROGRESS',
//     Done: 'DONE'
// }

const fileJsonToFile = (fileJson) => {
    /** @type TranslationFile */
    const file = {}
    file.title = fileJson.title
    file.url = fileJson.url
    file.size = fileJson.size ?? null

    return file
}